import { useQuery } from "@tanstack/react-query";

import supabase from "../lib/supabase";
import type { GovernanceActorProfile } from "../lib/tenantUserAccess";

/**
 * Profile fields needed for tenant governance UI and `effectiveTenantIdForActor`.
 * Uses `users_self` + super_admin cross-tenant select when applicable.
 */
export function useGovernanceActorProfile(userId: string | null | undefined): {
  profile: GovernanceActorProfile | null;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: ["governance-actor-profile", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      const result = await supabase
        .from("users")
        .select("id, role, tenant_id, platform_effective_tenant_id")
        .eq("id", userId ?? "")
        .maybeSingle();
      if (result.error) {
        throw new Error(result.error.message);
      }
      if (!result.data?.id || !result.data.tenant_id) {
        return null;
      }
      return result.data as GovernanceActorProfile;
    },
  });

  return {
    profile: query.data ?? null,
    isLoading: Boolean(userId) && query.data === undefined && !query.isError,
    isError: query.isError,
  };
}
